import React, { useCallback, useEffect, useState } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { ArrowDown, ArrowUp, Check, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * A B2C organisation's catalogue of minute packages. A package is sold as a top-up when
 * "sold separately" is on, and can be a plan's included package either way (only its
 * minutes count there). Top-up minutes work on every module of the buyer's plan.
 *
 * Minutes, price and currency are fixed when a package is created: a past purchase
 * must keep meaning what it cost, and the server's update operation has no field for
 * them. Offering a different price is deactivating one package and adding another.
 * Packages sold per module before plans existed are retired and shown read-only.
 */

export interface MinutePackage {
  id: number
  organisationId: number
  b2cModuleId: number | null
  retired: boolean
  soldSeparately: boolean
  name: string
  seconds: number
  /** Minor units: 4900 is 49.00 EUR. */
  priceAmount: number
  currency: string
  isActive: boolean
  sortOrder: number
}

export const PACKAGE_FIELDS = `
  id
  organisationId
  b2cModuleId
  retired
  soldSeparately
  name
  seconds
  priceAmount
  currency
  isActive
  sortOrder
`

export const GET_PACKAGES = gql`
  query getMinutePackages($organisationId: Int!) {
    getMinutePackages(organisationId: $organisationId) {
      ${PACKAGE_FIELDS}
    }
  }
`

const CREATE_PACKAGE = gql`
  mutation createMinutePackage($input: CreateMinutePackageInput!) {
    createMinutePackage(input: $input) {
      ${PACKAGE_FIELDS}
    }
  }
`

const UPDATE_PACKAGE = gql`
  mutation updateMinutePackage($input: UpdateMinutePackageInput!) {
    updateMinutePackage(input: $input) {
      ${PACKAGE_FIELDS}
    }
  }
`

/** Two-decimal currencies only, so a decimal price times 100 is always whole. */
export const CURRENCIES = ['eur', 'usd', 'gbp', 'chf', 'cad']

const toMinorUnits = (price: string): number | null => {
  const value = Number(price)
  if (price.trim() === '' || !Number.isFinite(value) || value <= 0) return null
  return Math.round(value * 100)
}

const fromMinorUnits = (amount: number) => (amount / 100).toFixed(2)

const errorMessage = (error: any, fallback: string) => error?.response?.errors?.[0]?.message || fallback

interface MinutePackagesPanelProps {
  organisationId: number
  /** Preselects the currency of the organisation's payment account, when it has one. */
  defaultCurrency?: string | null
  /** Current (not retired) packages, active or not, after every change. */
  onPackagesChange?: (packages: MinutePackage[]) => void
}

const MinutePackagesPanel: React.FC<MinutePackagesPanelProps> = ({ organisationId, defaultCurrency, onPackagesChange }) => {
  const [packages, setPackages] = useState<MinutePackage[]>([])
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Draft names, so typing does not save on every keystroke.
  const [names, setNames] = useState<Record<number, string>>({})

  const [newName, setNewName] = useState('')
  const [newMinutes, setNewMinutes] = useState('60')
  const [newPrice, setNewPrice] = useState('')
  const [newCurrency, setNewCurrency] = useState((defaultCurrency || 'eur').toLowerCase())
  const [newSoldSeparately, setNewSoldSeparately] = useState(true)
  const [adding, setAdding] = useState(false)

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true)
      const response = await authorizedGraphQLRequest<{ getMinutePackages: MinutePackage[] }>(GET_PACKAGES, { organisationId })
      setPackages(response.getMinutePackages || [])
      setNames({})
    } catch (err: any) {
      setError(errorMessage(err, 'Failed to load packages'))
    } finally {
      setLoading(false)
    }
  }, [organisationId])

  useEffect(() => {
    fetchPackages()
  }, [fetchPackages])

  useEffect(() => {
    onPackagesChange?.(packages.filter((p) => !p.retired))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packages])

  const current = packages.filter((p) => !p.retired)
  const retired = packages.filter((p) => p.retired)

  const replacePackage = (updated: MinutePackage) => {
    setPackages((current) => current.map((p) => (p.id === updated.id ? updated : p)).sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id))
    setNames((current) => {
      const { [updated.id]: _saved, ...rest } = current
      return rest
    })
  }

  const update = async (input: Record<string, unknown>) => {
    setError(null)
    try {
      setBusyId(Number(input.id))
      const response = await authorizedGraphQLRequest<{ updateMinutePackage: MinutePackage }>(UPDATE_PACKAGE, { input })
      replacePackage(response.updateMinutePackage)
    } catch (err: any) {
      setError(errorMessage(err, 'Failed to update the package'))
    } finally {
      setBusyId(null)
    }
  }

  const handleAdd = async () => {
    setError(null)
    const minutes = Number(newMinutes)
    const priceAmount = toMinorUnits(newPrice)
    if (!Number.isFinite(minutes) || minutes <= 0) {
      setError('Minutes must be greater than zero.')
      return
    }
    if (priceAmount === null) {
      setError('The price must be greater than zero.')
      return
    }

    try {
      setAdding(true)
      const response = await authorizedGraphQLRequest<{ createMinutePackage: MinutePackage }>(CREATE_PACKAGE, {
        input: {
          organisationId,
          name: newName.trim(),
          seconds: Math.round(minutes * 60),
          priceAmount,
          currency: newCurrency,
          sortOrder: current.length,
          soldSeparately: newSoldSeparately,
        },
      })
      setPackages((current) => [...current, response.createMinutePackage])
      setNewName('')
      setNewPrice('')
    } catch (err: any) {
      setError(errorMessage(err, 'Failed to create the package'))
    } finally {
      setAdding(false)
    }
  }

  /** Reordering is two sort orders swapped; the list is ordered by it on the server. */
  const move = async (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= current.length) return
    const a = current[index]
    const b = current[target]
    await update({ id: a.id, sortOrder: b.sortOrder === a.sortOrder ? target : b.sortOrder })
    await update({ id: b.id, sortOrder: index })
  }

  return (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <div className="flex items-center justify-between mb-2">
        <h6 className="text-sm font-semibold text-gray-800">Packages</h6>
        <span className="text-xs text-gray-500">{current.filter((p) => p.isActive && p.soldSeparately).length} sold as top-ups</span>
      </div>

      {error && <div className="mb-3 rounded-lg bg-red-50 border border-red-100 p-2 text-xs text-red-700">{error}</div>}

      {loading ? (
        <div className="py-3 text-xs text-gray-500">Loading packages…</div>
      ) : current.length === 0 ? (
        <p className="text-xs text-gray-500 mb-3">No packages yet. A plan needs one as its included minutes; subscribers see no top-ups until one is sold separately.</p>
      ) : (
        <ul className="space-y-2 mb-3">
          {current.map((pack, index) => {
            const draft = names[pack.id]
            const dirty = draft !== undefined && draft.trim() !== pack.name
            const busy = busyId === pack.id
            return (
              <li key={pack.id} className={`rounded-lg border border-gray-200 p-2 ${pack.isActive ? '' : 'opacity-60'}`}>
                <div className="flex items-center gap-2">
                  <input
                    value={draft ?? pack.name}
                    onChange={(e) => setNames((current) => ({ ...current, [pack.id]: e.target.value }))}
                    className="flex-1 min-w-0 px-2 py-1 border border-gray-200 rounded text-sm focus:ring-1 focus:ring-primary focus:border-transparent"
                  />
                  <Button size="sm" variant="outline" disabled={!dirty || busy} onClick={() => update({ id: pack.id, name: (draft ?? '').trim() })}>
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                  <button
                    type="button"
                    disabled={busy || index === 0}
                    onClick={() => move(index, -1)}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move up"
                  >
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    disabled={busy || index === current.length - 1}
                    onClick={() => move(index, 1)}
                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    title="Move down"
                  >
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                </div>
                <div className="mt-1 flex items-center justify-between text-xs text-gray-600">
                  <span className="tabular-nums">
                    {Math.round(pack.seconds / 60)} min · {fromMinorUnits(pack.priceAmount)} {pack.currency.toUpperCase()}
                  </span>
                  <span className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-1 cursor-pointer" title="Offered to subscribers as a top-up at this price">
                      <input type="checkbox" checked={pack.soldSeparately} disabled={busy} onChange={() => update({ id: pack.id, soldSeparately: !pack.soldSeparately })} />
                      Sold separately
                    </label>
                    <button type="button" disabled={busy} onClick={() => update({ id: pack.id, isActive: !pack.isActive })} className="text-primary hover:underline disabled:opacity-50">
                      {pack.isActive ? 'Deactivate' : 'Reactivate'}
                    </button>
                  </span>
                </div>
              </li>
            )
          })}
        </ul>
      )}

      {retired.length > 0 && (
        <details className="mb-3 text-xs text-gray-500">
          <summary className="cursor-pointer">Retired per-module packages ({retired.length})</summary>
          <ul className="mt-2 space-y-1">
            {retired.map((pack) => (
              <li key={pack.id} className="tabular-nums">
                {pack.name} · {Math.round(pack.seconds / 60)} min · {fromMinorUnits(pack.priceAmount)} {pack.currency.toUpperCase()}
              </li>
            ))}
          </ul>
          <p className="mt-1">Sold per module before plans. Kept for purchase history; they cannot be put back on sale.</p>
        </details>
      )}

      <div className="rounded-lg bg-gray-50 p-3">
        <p className="text-xs text-gray-500 mb-2">
          Minutes, price and currency cannot be changed later: a past purchase has to keep meaning what it cost. To change a price, deactivate the package and add another. A package only
          used as a plan's included minutes still needs a price; it is never charged unless the package is also sold separately.
        </p>
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-40 flex-1">
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Name</label>
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="One hour"
              className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Minutes</label>
            <input
              type="number"
              min={1}
              step={1}
              value={newMinutes}
              onChange={(e) => setNewMinutes(e.target.value)}
              className="w-20 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Price</label>
            <input
              type="number"
              min={0}
              step={0.01}
              value={newPrice}
              onChange={(e) => setNewPrice(e.target.value)}
              placeholder="49.00"
              className="w-24 px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Currency</label>
            <select
              value={newCurrency}
              onChange={(e) => setNewCurrency(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded text-sm bg-white uppercase focus:ring-1 focus:ring-primary focus:border-transparent"
            >
              {CURRENCIES.map((code) => (
                <option key={code} value={code}>
                  {code.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-600 pb-2">
            <input type="checkbox" checked={newSoldSeparately} onChange={(e) => setNewSoldSeparately(e.target.checked)} />
            Sold separately
          </label>
          <Button size="sm" onClick={handleAdd} disabled={adding || !newName.trim() || !newPrice.trim()}>
            <Plus className="h-3.5 w-3.5 mr-1" />
            {adding ? 'Adding…' : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default MinutePackagesPanel
